// Client / Case Geography — US heatmap by POLICY state + drill-down county map per state.
import { useState, useEffect, useCallback, useMemo } from 'react';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import { feature } from 'topojson-client';
import { geoAlbers } from 'd3-geo';
import { Map as MapIcon, Loader2, Users, FileText, MapPin, ArrowLeft, ExternalLink, AlertTriangle, Pencil } from 'lucide-react';
import portalApi from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import usStates from '../assets/usStates.json';
import usCounties from '../assets/usCounties.json';
import { toast } from 'sonner';

// Precompute county/state geography once (module scope).
const STATES_FC = feature(usCounties, usCounties.objects.states);
const COUNTIES_FC = feature(usCounties, usCounties.objects.counties);
const STATE_FIPS = {};
STATES_FC.features.forEach((f) => { STATE_FIPS[f.properties.name] = String(f.id).padStart(2, '0'); });
const normCounty = (s) =>
  (s || '').toString().trim().toLowerCase()
    .replace(/\s+(county|parish|borough|census area|city and borough|municipality|city)$/i, '')
    .trim();

const colorFor = (count, max) => {
  if (!count) return '#e2e8f0';
  const ratio = max > 0 ? count / max : 0;
  const hue = 210 - 210 * ratio; // blue (cool) -> red (hot)
  return `hsl(${hue}, 80%, ${62 - ratio * 16}%)`;
};

// County-level choropleth for a single selected state.
function StateCountyMap({ stateName, byCounty, onCountyClick, activeCounty }) {
  const fips = STATE_FIPS[stateName];
  const [hover, setHover] = useState(null);

  const { fc, projection } = useMemo(() => {
    const feats = COUNTIES_FC.features.filter((f) => String(f.id).slice(0, 2) === fips);
    const collection = { type: 'FeatureCollection', features: feats };
    const proj = feats.length ? geoAlbers().fitSize([640, 420], collection) : null;
    return { fc: collection, projection: proj };
  }, [fips]);

  const counts = useMemo(() => {
    const m = {};
    (byCounty || []).forEach((r) => { m[normCounty(r.key)] = (m[normCounty(r.key)] || 0) + r.count; });
    return m;
  }, [byCounty]);
  const max = useMemo(() => Math.max(1, ...(byCounty || []).map((r) => r.count)), [byCounty]);

  if (!fips || !projection) {
    return <p className="text-center text-slate-400 py-16 text-sm">No county map available for {stateName}.</p>;
  }

  return (
    <>
      <div className="min-h-[420px]" data-testid="geo-county-map">
        <ComposableMap projection={projection} width={640} height={420} style={{ width: '100%', height: 'auto' }}>
          <Geographies geography={fc}>
            {({ geographies }) => geographies.map((geo) => {
              const cn = geo.properties.name;
              const count = counts[normCounty(cn)] || 0;
              const isActive = activeCounty && normCounty(cn) === normCounty(activeCounty);
              return (
                <Geography key={geo.rsmKey} geography={geo}
                  fill={colorFor(count, max)} stroke={isActive ? '#0f172a' : '#ffffff'} strokeWidth={isActive ? 1.4 : 0.4}
                  onMouseEnter={() => setHover({ name: cn, count })}
                  onMouseLeave={() => setHover(null)}
                  onClick={() => onCountyClick && onCountyClick(cn)}
                  style={{ default: { outline: 'none' }, hover: { outline: 'none', opacity: 0.85, cursor: 'pointer' }, pressed: { outline: 'none' } }}
                  data-testid={`geo-county-${normCounty(cn).replace(/\s+/g, '-')}`} />
              );
            })}
          </Geographies>
        </ComposableMap>
      </div>
      <div className="flex items-center justify-between mt-2">
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <span>Fewer</span>
          <div className="h-2 w-40 rounded-full" style={{ background: 'linear-gradient(90deg, hsl(210,80%,62%), hsl(105,80%,54%), hsl(0,80%,46%))' }} />
          <span>More</span>
        </div>
        <div className="text-sm font-medium text-slate-700" data-testid="geo-county-hover">
          {hover ? `${hover.name} County: ${hover.count.toLocaleString()}` : 'Hover a county'}
        </div>
      </div>
    </>
  );
}

export function GeographyPage() {
  const { workspaces, activeWorkspaceId, user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [metric, setMetric] = useState('clients');
  const [filters, setFilters] = useState({
    workspace_id: isAdmin ? 'all' : (activeWorkspaceId || 'all'),
    coverage_year_id: 'all', policy_status: 'all', carrier: 'all', product_type: 'all', state: 'all', city: '', county: '',
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hover, setHover] = useState(null);
  const [constants, setConstants] = useState(null);
  const [years, setYears] = useState([]);
  const [carriers, setCarriers] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [c, y, ca] = await Promise.all([portalApi.getConstants(), portalApi.getCoverageYears(), portalApi.getCarriers()]);
        setConstants(c); setYears(y || []); setCarriers(ca || []);
      } catch (e) { /* non-blocking */ }
    })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { metric, ...filters, city: filters.city || undefined, county: filters.county || undefined };
      setData(await portalApi.getGeography(params));
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  }, [metric, filters]);

  useEffect(() => { load(); }, [load]);

  const setF = (k, v) => setFilters((p) => ({ ...p, [k]: v }));
  const drillState = filters.state !== 'all' ? filters.state : null;
  const navigate = useNavigate();

  // Drill-through: actual clients/cases in the selected state (+ optional county).
  const [drillCounty, setDrillCounty] = useState(null);
  const [entities, setEntities] = useState(null);
  const [entitiesLoading, setEntitiesLoading] = useState(false);
  const [unknownOpen, setUnknownOpen] = useState(false); // "without a location" list

  useEffect(() => { setDrillCounty(null); }, [filters.state, metric]);
  useEffect(() => { if (drillState) setUnknownOpen(false); }, [drillState]);

  const entityState = unknownOpen ? '__none__' : drillState;

  useEffect(() => {
    if (!entityState) { setEntities(null); return; }
    let cancelled = false;
    (async () => {
      setEntitiesLoading(true);
      try {
        const params = {
          metric, state: entityState, county: unknownOpen ? undefined : (drillCounty || undefined),
          workspace_id: filters.workspace_id, coverage_year_id: filters.coverage_year_id,
          policy_status: filters.policy_status, carrier: filters.carrier, product_type: filters.product_type,
          city: filters.city || undefined,
        };
        const res = await portalApi.getGeographyEntities(params);
        if (!cancelled) setEntities(res);
      } catch (e) { if (!cancelled) toast.error(e.message); }
      finally { if (!cancelled) setEntitiesLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [entityState, unknownOpen, drillCounty, metric, filters.workspace_id, filters.coverage_year_id, filters.policy_status, filters.carrier, filters.product_type, filters.city]);

  const toggleCounty = (cn) => setDrillCounty((prev) => (prev && normCounty(prev) === normCounty(cn) ? null : cn));

  // Inline address editing from the drill-through table.
  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [savingAddr, setSavingAddr] = useState(false);

  const openEdit = (row) => {
    setEditForm({
      street_address: '', apartment: '', city: '', state: drillState || '', zip_code: '', county: '',
    });
    setEditRow(row);
  };

  const refreshEntities = useCallback(async () => {
    if (!entityState) return;
    try {
      const res = await portalApi.getGeographyEntities({
        metric, state: entityState, county: unknownOpen ? undefined : (drillCounty || undefined),
        workspace_id: filters.workspace_id, coverage_year_id: filters.coverage_year_id,
        policy_status: filters.policy_status, carrier: filters.carrier, product_type: filters.product_type, city: filters.city || undefined,
      });
      setEntities(res);
    } catch (e) { /* ignore */ }
  }, [entityState, unknownOpen, drillCounty, metric, filters]);

  const saveAddress = async () => {
    if (!editRow?.client_id) return;
    setSavingAddr(true);
    try {
      const demographics = {};
      ['street_address', 'apartment', 'city', 'state', 'zip_code', 'county'].forEach((k) => {
        if (editForm[k] && editForm[k].trim()) demographics[k] = editForm[k].trim();
      });
      await portalApi.updateClient(editRow.client_id, { demographics });
      toast.success('Address updated');
      setEditRow(null);
      await Promise.all([refreshEntities(), load()]);
    } catch (e) {
      toast.error(e.message || 'Error saving address');
    } finally {
      setSavingAddr(false);
    }
  };

  const stateCounts = useMemo(() => {
    const m = {};
    (data?.by_state || []).forEach((s) => { m[s.state] = s.count; });
    return m;
  }, [data]);
  const maxState = useMemo(() => Math.max(1, ...(data?.by_state || []).map((s) => s.count)), [data]);

  const MetricBtn = ({ value, label, icon: Icon }) => (
    <button type="button" onClick={() => setMetric(value)}
      className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border transition-colors ${metric === value ? 'bg-sky-500 text-white border-sky-500' : 'bg-white text-slate-600 border-slate-200 hover:border-sky-300'}`}
      data-testid={`metric-${value}`}>
      <Icon className="h-4 w-4" />{label}
    </button>
  );

  const rankingRows = drillState ? (data?.by_county || []) : (data?.by_state || []);

  return (
    <div className="space-y-6" data-testid="geography-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><MapIcon className="h-6 w-6 text-sky-500" /> Geography</h1>
          <p className="text-slate-500 text-sm">
            {loading ? 'Loading…' : `${(data?.total ?? 0).toLocaleString()} ${metric === 'clients' ? 'clients' : 'cases'} mapped by policy state`}
            {data?.unknown_location ? (
              <>
                {' · '}
                <button type="button" onClick={() => { setF('state', 'all'); setUnknownOpen(true); }}
                  className="text-amber-600 hover:text-amber-700 font-medium underline underline-offset-2"
                  data-testid="geo-unknown-link">
                  {data.unknown_location} without a location
                </button>
              </>
            ) : ''}
          </p>
        </div>
        <div className="flex items-center gap-2" data-testid="metric-toggle">
          <MetricBtn value="clients" label="Clients" icon={Users} />
          <MetricBtn value="cases" label="Cases" icon={FileText} />
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2" data-testid="geo-filters">
        {isAdmin && (
          <Select value={filters.workspace_id} onValueChange={(v) => setF('workspace_id', v)}>
            <SelectTrigger className="w-[190px]" data-testid="geo-filter-workspace"><SelectValue placeholder="Workspace" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Workspaces</SelectItem>
              {workspaces.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Select value={filters.coverage_year_id} onValueChange={(v) => setF('coverage_year_id', v)}>
          <SelectTrigger className="w-[170px]" data-testid="geo-filter-year"><SelectValue placeholder="Coverage Year" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Years</SelectItem>
            {years.map((y) => <SelectItem key={y.id} value={y.id}>{y.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.policy_status} onValueChange={(v) => setF('policy_status', v)}>
          <SelectTrigger className="w-[160px]" data-testid="geo-filter-policy-status"><SelectValue placeholder="Policy Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {(constants?.policy_statuses || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.carrier} onValueChange={(v) => setF('carrier', v)}>
          <SelectTrigger className="w-[160px]" data-testid="geo-filter-carrier"><SelectValue placeholder="Carrier" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Carriers</SelectItem>
            {carriers.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.product_type} onValueChange={(v) => setF('product_type', v)}>
          <SelectTrigger className="w-[170px]" data-testid="geo-filter-product"><SelectValue placeholder="Product" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Products</SelectItem>
            {(constants?.product_types || []).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.state} onValueChange={(v) => setF('state', v)}>
          <SelectTrigger className="w-[150px]" data-testid="geo-filter-state"><SelectValue placeholder="State" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All States</SelectItem>
            {(constants?.us_states || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input value={filters.city} onChange={(e) => setF('city', e.target.value)} placeholder="City" className="w-[140px]" data-testid="geo-filter-city" />
        <Input value={filters.county} onChange={(e) => setF('county', e.target.value)} placeholder="County" className="w-[140px]" data-testid="geo-filter-county" />
        <Button variant="ghost" onClick={() => setFilters({ workspace_id: isAdmin ? 'all' : (activeWorkspaceId || 'all'), coverage_year_id: 'all', policy_status: 'all', carrier: 'all', product_type: 'all', state: 'all', city: '', county: '' })} data-testid="geo-reset">Reset</Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Map */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-4 relative" data-testid="geo-map">
          {loading && <div className="absolute inset-0 bg-white/60 flex items-center justify-center z-10"><Loader2 className="h-6 w-6 animate-spin text-sky-500" /></div>}

          {drillState ? (
            <>
              <div className="flex items-center justify-between mb-3">
                <button type="button" onClick={() => setF('state', 'all')}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-sky-600 hover:text-sky-700"
                  data-testid="geo-back-to-us">
                  <ArrowLeft className="h-4 w-4" /> Back to US
                </button>
                <h3 className="text-sm font-semibold text-slate-900" data-testid="geo-drill-title">{drillState}{drillCounty ? ` · ${drillCounty} County` : ''} · counties by {metric === 'clients' ? 'clients' : 'cases'}</h3>
              </div>
              <StateCountyMap stateName={drillState} byCounty={data?.by_county} onCountyClick={toggleCounty} activeCounty={drillCounty} />
            </>
          ) : (
            <>
              <div className="min-h-[420px]">
                <ComposableMap projection="geoAlbersUsa" style={{ width: '100%', height: 'auto' }}>
                  <Geographies geography={usStates}>
                    {({ geographies }) => geographies.map((geo) => {
                      const name = geo.properties.name;
                      const count = stateCounts[name] || 0;
                      return (
                        <Geography key={geo.rsmKey} geography={geo}
                          fill={colorFor(count, maxState)}
                          stroke="#ffffff" strokeWidth={0.5}
                          onMouseEnter={() => setHover({ name, count })}
                          onMouseLeave={() => setHover(null)}
                          onClick={() => setF('state', name)}
                          style={{ default: { outline: 'none' }, hover: { outline: 'none', opacity: 0.85, cursor: 'pointer' }, pressed: { outline: 'none' } }}
                          data-testid={`geo-state-${name.replace(/\s+/g, '-').toLowerCase()}`} />
                      );
                    })}
                  </Geographies>
                </ComposableMap>
              </div>
              <div className="flex items-center justify-between mt-2">
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <span>Fewer</span>
                  <div className="h-2 w-40 rounded-full" style={{ background: 'linear-gradient(90deg, hsl(210,80%,62%), hsl(105,80%,54%), hsl(0,80%,46%))' }} />
                  <span>More</span>
                </div>
                <div className="text-sm font-medium text-slate-700" data-testid="geo-hover">
                  {hover ? `${hover.name}: ${hover.count.toLocaleString()}` : 'Hover a state · click to drill down'}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Ranking (states, or counties when drilled into a state) */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="geo-state-ranking">
          <div className="px-4 py-3 border-b border-slate-100 font-semibold text-slate-900 text-sm">
            {drillState ? `${drillState} counties by ${metric === 'clients' ? 'clients' : 'cases'}` : `States by ${metric === 'clients' ? 'clients' : 'cases'}`}
          </div>
          <div className="max-h-[430px] overflow-y-auto">
            {rankingRows.length === 0 ? (
              <p className="text-center text-slate-400 py-10 text-sm">No data.</p>
            ) : drillState ? (
              rankingRows.map((s, i) => {
                const active = drillCounty && normCounty(drillCounty) === normCounty(s.key);
                return (
                  <button key={s.key} type="button" onClick={() => toggleCounty(s.key)}
                    className={`w-full flex items-center justify-between px-4 py-2.5 text-left border-b border-slate-50 hover:bg-slate-50 ${active ? 'bg-sky-50' : ''}`}
                    data-testid={`geo-county-row-${normCounty(s.key).replace(/\s+/g, '-')}`}>
                    <span className="text-sm text-slate-700"><span className="text-slate-400 mr-2">{i + 1}.</span>{s.key}{active ? ' ✓' : ''}</span>
                    <span className="text-sm font-semibold text-slate-900">{s.count.toLocaleString()}</span>
                  </button>
                );
              })
            ) : (
              rankingRows.map((s, i) => (
                <button key={s.state} type="button" onClick={() => setF('state', s.state)}
                  className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-slate-50 text-left border-b border-slate-50">
                  <span className="text-sm text-slate-700"><span className="text-slate-400 mr-2">{i + 1}.</span>{s.state}</span>
                  <span className="text-sm font-semibold text-slate-900">{s.count.toLocaleString()}</span>
                </button>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Drill-through: actual clients/cases in the selected state (+ county) or without a location */}
      {(drillState || unknownOpen) && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="geo-entities">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
            <div className="font-semibold text-slate-900 text-sm">
              {unknownOpen
                ? `${metric === 'clients' ? 'Clients' : 'Cases'} without a location`
                : `${metric === 'clients' ? 'Clients' : 'Cases'} in ${drillState}${drillCounty ? ` · ${drillCounty} County` : ''}`}
              <span className="text-slate-400 font-normal ml-2">{entitiesLoading ? '…' : `${(entities?.total ?? 0).toLocaleString()} total`}</span>
            </div>
            {unknownOpen ? (
              <button type="button" onClick={() => setUnknownOpen(false)} className="text-xs font-medium text-sky-600 hover:text-sky-700" data-testid="geo-close-unknown">Close</button>
            ) : drillCounty && (
              <button type="button" onClick={() => setDrillCounty(null)} className="text-xs font-medium text-sky-600 hover:text-sky-700" data-testid="geo-clear-county">Clear county filter</button>
            )}
          </div>
          <div className="max-h-[460px] overflow-y-auto">
            {entitiesLoading ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-sky-500" /></div>
            ) : (entities?.items || []).length === 0 ? (
              <p className="text-center text-slate-400 py-10 text-sm">No {metric} found.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide sticky top-0">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Client ID</th>
                    <th className="text-left px-4 py-2 font-medium">Name</th>
                    <th className="text-left px-4 py-2 font-medium">Athena ID</th>
                    {metric === 'cases' && <th className="text-left px-4 py-2 font-medium">Coverage Year</th>}
                    <th className="text-left px-4 py-2 font-medium">Address on file</th>
                    <th className="px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {(entities?.items || []).map((r) => (
                    <tr key={r.client_id + (r.case_id || '')} className="border-t border-slate-50 hover:bg-slate-50" data-testid={`geo-entity-row-${r.calyvra_client_id || r.athena_id}`}>
                      <td className="px-4 py-2.5 text-slate-700 font-medium whitespace-nowrap">{r.calyvra_client_id || '—'}</td>
                      <td className="px-4 py-2.5 text-slate-700">{r.name || '—'}</td>
                      <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap">{r.athena_id || '—'}</td>
                      {metric === 'cases' && <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap">{r.coverage_year_label || '—'}</td>}
                      <td className="px-4 py-2.5">
                        {r.has_address ? (
                          <span className="text-slate-600">{r.address}{r.county ? ` · ${r.county} County` : ''}</span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-amber-600 font-medium" data-testid="geo-entity-noaddr">
                            <AlertTriangle className="h-3.5 w-3.5" /> No address on file
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <div className="flex items-center justify-end gap-3">
                          <button type="button" onClick={() => openEdit(r)}
                            className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-800 font-medium text-xs whitespace-nowrap"
                            data-testid={`geo-entity-edit-${r.calyvra_client_id || r.athena_id}`}>
                            <Pencil className="h-3.5 w-3.5" /> Edit address
                          </button>
                          {r.client_id && (
                            <button type="button" onClick={() => navigate(`/portal/clients/${r.client_id}`)}
                              className="inline-flex items-center gap-1 text-sky-600 hover:text-sky-700 font-medium text-xs whitespace-nowrap"
                              data-testid={`geo-entity-open-${r.calyvra_client_id || r.athena_id}`}>
                              Open <ExternalLink className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Top ZIPs + Cities + Counties */}
      <div className="grid md:grid-cols-3 gap-6">
        <TableCard title={drillState ? `Top ZIP Codes in ${drillState}` : 'Top ZIP Codes'} icon={MapPin} rows={data?.by_zip} render={(r) => (
          <><span className="text-sm text-slate-700">{r.key}<span className="text-slate-400 text-xs ml-2">{[r.city, r.state].filter(Boolean).join(', ')}</span></span><span className="text-sm font-semibold">{r.count}</span></>
        )} testId="geo-top-zips" />
        <TableCard title={drillState ? `Top Cities in ${drillState}` : 'Top Cities'} rows={data?.by_city} render={(r) => (
          <><span className="text-sm text-slate-700">{r.key}</span><span className="text-sm font-semibold">{r.count}</span></>
        )} testId="geo-top-cities" />
        <TableCard title={drillState ? `Top Counties in ${drillState}` : 'Top Counties'} rows={data?.by_county} render={(r) => (
          <><span className="text-sm text-slate-700">{r.key}</span><span className="text-sm font-semibold">{r.count}</span></>
        )} testId="geo-top-counties" />
      </div>

      <Dialog open={Boolean(editRow)} onOpenChange={(o) => !o && setEditRow(null)}>
        <DialogContent className="max-w-lg" data-testid="geo-edit-address-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Pencil className="h-4 w-4 text-sky-500" />Edit address · {editRow?.calyvra_client_id || editRow?.athena_id}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1.5">
              <Label>Street address</Label>
              <Input value={editForm.street_address || ''} onChange={(e) => setEditForm((p) => ({ ...p, street_address: e.target.value }))} data-testid="geo-edit-street" />
            </div>
            <div className="space-y-1.5">
              <Label>Apartment / Unit</Label>
              <Input value={editForm.apartment || ''} onChange={(e) => setEditForm((p) => ({ ...p, apartment: e.target.value }))} data-testid="geo-edit-apartment" />
            </div>
            <div className="space-y-1.5">
              <Label>City</Label>
              <Input value={editForm.city || ''} onChange={(e) => setEditForm((p) => ({ ...p, city: e.target.value }))} data-testid="geo-edit-city" />
            </div>
            <div className="space-y-1.5">
              <Label>State</Label>
              <Select value={editForm.state || ''} onValueChange={(v) => setEditForm((p) => ({ ...p, state: v }))}>
                <SelectTrigger data-testid="geo-edit-state"><SelectValue placeholder="Select state" /></SelectTrigger>
                <SelectContent>{(constants?.us_states || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>ZIP code</Label>
              <Input value={editForm.zip_code || ''} onChange={(e) => setEditForm((p) => ({ ...p, zip_code: e.target.value }))} data-testid="geo-edit-zip" />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>County</Label>
              <Input value={editForm.county || ''} onChange={(e) => setEditForm((p) => ({ ...p, county: e.target.value }))} data-testid="geo-edit-county" />
            </div>
          </div>
          <p className="text-xs text-slate-400">Only the fields you fill will be updated (non-destructive). Leave blank to keep existing values.</p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditRow(null)} disabled={savingAddr}>Cancel</Button>
            <Button onClick={saveAddress} disabled={savingAddr} data-testid="geo-edit-save">
              {savingAddr ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Save address
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TableCard({ title, icon: Icon, rows, render, testId }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={testId}>
      <div className="px-4 py-3 border-b border-slate-100 font-semibold text-slate-900 text-sm flex items-center gap-2">
        {Icon ? <Icon className="h-4 w-4 text-sky-500" /> : null}{title}
      </div>
      <div className="max-h-[300px] overflow-y-auto">
        {(rows || []).length === 0 ? (
          <p className="text-center text-slate-400 py-8 text-sm">No data.</p>
        ) : (rows || []).map((r, i) => (
          <div key={`${r.key}-${i}`} className="flex items-center justify-between px-4 py-2 border-b border-slate-50">{render(r)}</div>
        ))}
      </div>
    </div>
  );
}

export default GeographyPage;
